// Shared EZ-ARON UI atoms — stage identity (colors/labels), deadline pill,
// money formatting and the decision segmented control. The module accent is
// rose/pink (mirrors the DLC_07 badge gradient in utils/dlc.ts).

import { clsx } from "clsx";

import { useEzDecision } from "./hooks";
import type { FunnelStage, ProceedingRow, UserDecision } from "./types";

export const fmtPln = (v: string | null | undefined) =>
  v == null
    ? "—"
    : `${Number(v).toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} zł`;

export const fmtDate = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleDateString("pl-PL") : "—";

export const STAGE_META: Record<
  FunnelStage,
  { label: string; chip: string; rail: string }
> = {
  s2_qualified: {
    label: "Priorytet",
    chip: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
    rail: "border-l-emerald-500",
  },
  s1_candidate: {
    label: "Czeka na AI",
    chip: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
    rail: "border-l-amber-400",
  },
  s1_classified: {
    label: "Poza profilem",
    chip: "bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300",
    rail: "border-l-sky-400",
  },
  s1_rejected: {
    label: "Odrzucone przez AI",
    chip: "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400",
    rail: "border-l-gray-300 dark:border-l-gray-600",
  },
  rejected_rules: {
    label: "Odrzucone regułami",
    chip: "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400",
    rail: "border-l-gray-300 dark:border-l-gray-600",
  },
};

export const TAG_LABEL: Record<string, string> = {
  elektroenergetyka: "Elektroenergetyka",
  oswietlenie: "Oświetlenie",
  stacje_trafo: "Stacje trafo",
  oze_pv: "PV / OZE",
  magazyny_energii: "Magazyny energii",
  ladowarki_ev: "Ładowarki EV",
  elektryka_kubaturowa: "Elektryka kubaturowa",
  teletechnika: "Teletechnika",
  drogowe: "Drogowe",
  sanitarne: "Sanitarne",
  termomodernizacja: "Termomodernizacja",
  kubatura_ogolna: "Kubatura ogólna",
  inne: "Inne",
};

/* BZP koduje województwo jako PL + kod TERYT województwa. */
export const PROVINCES: { code: string; name: string }[] = [
  { code: "PL02", name: "dolnośląskie" },
  { code: "PL04", name: "kujawsko-pomorskie" },
  { code: "PL06", name: "lubelskie" },
  { code: "PL08", name: "lubuskie" },
  { code: "PL10", name: "łódzkie" },
  { code: "PL12", name: "małopolskie" },
  { code: "PL14", name: "mazowieckie" },
  { code: "PL16", name: "opolskie" },
  { code: "PL18", name: "podkarpackie" },
  { code: "PL20", name: "podlaskie" },
  { code: "PL22", name: "pomorskie" },
  { code: "PL24", name: "śląskie" },
  { code: "PL26", name: "świętokrzyskie" },
  { code: "PL28", name: "warmińsko-mazurskie" },
  { code: "PL30", name: "wielkopolskie" },
  { code: "PL32", name: "zachodniopomorskie" },
];

export const provinceName = (code: string) =>
  PROVINCES.find((p) => p.code === code)?.name ?? code;

export const WORK_MODE_META: Record<string, { label: string; chip: string }> = {
  P: {
    label: "Projekt",
    chip: "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300",
  },
  PW: {
    label: "Zaprojektuj i wybuduj",
    chip: "bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300",
  },
  W: {
    label: "Wybuduj",
    chip: "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300",
  },
};

export function WorkModeChip({ mode }: { mode: string }) {
  const m = WORK_MODE_META[mode];
  if (!m) return null;
  return (
    <span className={clsx("text-[10px] font-semibold rounded-full px-2 py-0.5 whitespace-nowrap", m.chip)}>
      {m.label}
    </span>
  );
}

export function TagChip({ tag }: { tag: string }) {
  return (
    <span className="text-[10px] font-medium rounded-full px-2 py-0.5 bg-rose-50 text-rose-700 ring-1 ring-inset ring-rose-200 dark:bg-rose-900/30 dark:text-rose-300 dark:ring-rose-800">
      {TAG_LABEL[tag] ?? tag}
    </span>
  );
}

export function StageChip({ stage }: { stage: FunnelStage }) {
  const m = STAGE_META[stage];
  return (
    <span className={clsx("text-[10px] font-medium rounded-full px-2 py-0.5 whitespace-nowrap", m.chip)}>
      {m.label}
    </span>
  );
}

/* Deadline pill — urgency-colored countdown to the offer deadline. */
export function DeadlinePill({ row }: { row: ProceedingRow }) {
  if (row.has_result)
    return (
      <span
        className={clsx(
          "text-[11px] font-medium rounded-full px-2 py-0.5 whitespace-nowrap",
          row.is_annulled
            ? "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400"
            : "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300",
        )}
      >
        {row.is_annulled ? "Unieważnione" : "Rozstrzygnięte"}
      </span>
    );
  const d = row.days_left;
  if (d == null)
    return <span className="text-[11px] text-[var(--color-text-muted)]">brak terminu</span>;
  if (d < 0)
    return (
      <span className="text-[11px] rounded-full px-2 py-0.5 bg-gray-100 text-gray-500 dark:bg-gray-800">
        po terminie
      </span>
    );
  const cls =
    d <= 5
      ? "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300"
      : d <= 14
        ? "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
        : "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300";
  return (
    <span
      className={clsx("text-[11px] font-semibold rounded-full px-2 py-0.5 whitespace-nowrap tabular-nums", cls)}
      title={`Termin składania ofert: ${fmtDate(row.submitting_offers_date)}`}
    >
      {d === 0 ? "dziś!" : `${d} dni`}
    </span>
  );
}

const DECISIONS: { value: Exclude<UserDecision, "">; label: string; active: string }[] = [
  { value: "start", label: "Startujemy", active: "bg-emerald-600 text-white" },
  { value: "watch", label: "Obserwujemy", active: "bg-sky-600 text-white" },
  { value: "pass", label: "Odpuszczamy", active: "bg-gray-500 text-white" },
];

/* Human decision — a segmented control; click the active segment to clear. */
export function DecisionControl({ row }: { row: ProceedingRow }) {
  const decide = useEzDecision();
  return (
    <div
      className="inline-flex rounded-lg overflow-hidden border border-[var(--color-border)] divide-x divide-[var(--color-border)]"
      role="group"
      aria-label="Decyzja"
    >
      {DECISIONS.map((d) => (
        <button
          key={d.value}
          disabled={decide.isPending}
          onClick={() =>
            decide.mutate({ id: row.id, decision: row.user_decision === d.value ? "" : d.value })
          }
          className={clsx(
            "text-[11px] px-2.5 py-1 transition-colors",
            row.user_decision === d.value
              ? d.active
              : "bg-transparent text-[var(--color-text-secondary)] hover:bg-black/5 dark:hover:bg-white/10",
          )}
        >
          {d.label}
        </button>
      ))}
    </div>
  );
}

/* Segmented filter control (scope switch etc.). */
export function Segmented<T extends string>({
  options,
  value,
  onChange,
}: {
  options: { value: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div className="inline-flex rounded-lg overflow-hidden border border-[var(--color-border)] divide-x divide-[var(--color-border)]">
      {options.map((o) => (
        <button
          key={o.value}
          onClick={() => onChange(o.value)}
          className={clsx(
            "text-xs px-3 py-1.5 transition-colors",
            value === o.value
              ? "bg-rose-600 text-white"
              : "bg-transparent text-[var(--color-text-secondary)] hover:bg-black/5 dark:hover:bg-white/10",
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
